import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MailCheck } from 'lucide-react';
import { Logo } from '@/components/layout/Logo';
import { Button } from '@/components/ui/Button';
import { authApi } from '@/api/endpoints';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { getApiErrorMessage } from '@/api/client';
import { useSeo } from '@/hooks/useSeo';
import { sendSignupAttribution } from '@/lib/analytics';

const CODE_RE = /^[0-9]{6}$/;

export function VerifyEmail() {
  // Shaxsiy/JS-only sahifa: indekslanmaydi; oldingi sahifaning JSON-LD i tozalanadi
  useSeo({ title: "Emailni tasdiqlash — SQLMaster.uz", noindex: true });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const verifyEmail = useAuthStore((s) => s.verifyEmail);
  const push = useToastStore((s) => s.push);

  const email = (location.state as { email?: string } | null)?.email ?? '';

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Email yo'q bo'lsa (sahifaga to'g'ridan-to'g'ri kirilgan) — registerga qaytaramiz
  useEffect(() => {
    if (!email) navigate('/register', { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!CODE_RE.test(code)) {
      setError(t('auth.errorCode'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await verifyEmail(email, code);
      sendSignupAttribution();
      push(t('auth.verified'), 'success');
      navigate('/profile');
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await authApi.resendVerification(email);
      push(t('auth.codeResent'), 'success');
      setCooldown(60);
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 flex justify-center">
        <Logo />
      </div>

      <div className="card p-6 sm:p-8">
        <div className="mb-4 flex justify-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/15">
            <MailCheck className="h-6 w-6 text-brand-600" />
          </span>
        </div>
        <h1 className="mb-1 text-center text-2xl font-bold">{t('auth.verifyTitle')}</h1>
        <p className="mb-6 text-center text-sm text-content-secondary">
          {t('auth.verifySubtitle')} <span className="font-medium text-content">{email}</span>
        </p>

        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('auth.verifyCodeLabel')}</label>
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="input text-center text-2xl tracking-[0.5em]"
              autoFocus
            />
            {error && <p className="mt-1 text-xs text-[var(--error)]">{error}</p>}
          </div>

          <Button type="submit" loading={loading} className="w-full">
            {t('auth.verifyButton')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-content-secondary">
          <button
            type="button"
            onClick={resend}
            disabled={cooldown > 0}
            className="font-medium text-brand-600 hover:underline disabled:cursor-not-allowed disabled:text-content-muted disabled:no-underline"
          >
            {cooldown > 0 ? t('auth.resendIn', { s: cooldown }) : t('auth.resendCode')}
          </button>
        </div>

        <p className="mt-4 text-center text-sm text-content-secondary">
          <Link to="/register" className="font-medium text-brand-600 hover:underline">
            {t('auth.backToRegister')}
          </Link>
        </p>
      </div>
    </div>
  );
}
